import React, { useMemo, useState } from "react";

import { Maximize2, X } from "lucide-react";

import { buildRingsContactsForAccount } from "./myRelationshipsDataAdapter";
import RelationshipRingsFullModal from "./RelationshipRingsFullModal";
import {
  RelationshipRingsGraph,
  RelationshipRingsLegend,
} from "./RelationshipRingsGraph";
import {
  DEFAULT_FAN_OUT_CAP,
  fanCapTone,
} from "../../../Utils/Helper/rlFanCaps";
import {
  formatAggregateRl,
  getAccountIdentityBadgeStyle,
  getAccountIdentityColor,
  getRlBandPillStyle,
} from "../account-relationships/relationshipUtils";

export default function AccountPeekOverlay({
  account,
  considerMeddpiccRole = false,
  onClose,
  onOpenAccount,
  onAssessRingContact,
  userName,
  centerUserStakeholder,
  viewerLabel,
  fanOutCap = DEFAULT_FAN_OUT_CAP,
}) {
  const [fullViewOpen, setFullViewOpen] = useState(false);
  const panelContacts = useMemo(
    () => buildRingsContactsForAccount(account, { includeUnmapped: false }),
    [account]
  );
  const fullContacts = useMemo(
    () => buildRingsContactsForAccount(account, { includeUnmapped: true }),
    [account]
  );
  const pendingCount = panelContacts.filter(
    contact => contact.rlLevel === null || contact.rlLevel === undefined
  ).length;
  const assessedCount = panelContacts.length - pendingCount;
  const myMapped =
    account?.myMappedPairs != null
      ? Number(account.myMappedPairs)
      : panelContacts.length;
  const fanChipClass = fanCapTone(myMapped, fanOutCap);

  return (
    <>
      <div className="t3-l2-modal-backdrop" />
      <div
        className="t3-l2-modal"
        role="dialog"
        aria-label={`${account.name} quick look`}
      >
        <div className="t3-l2-hdr">
          <div className="t3-l2-hdr-title">
            <span
              className="t3-l2-hdr-logo"
              style={getAccountIdentityBadgeStyle(account.id, {
                color:
                  account.sectorColor || getAccountIdentityColor(account.id),
              })}
            >
              {account.initials?.slice(0, 2) || "?"}
            </span>
            <span>{account.name}</span>
            <span className="t3-l2-hdr-sub">
              {viewerLabel
                ? `Quick look · ${viewerLabel.split(" ")[0]}'s mapped contacts`
                : "Quick look · your mapped contacts"}
            </span>
          </div>
          <div className="t3-l2-hdr-actions">
            <button
              type="button"
              className="t3-l2-hdr-btn t3-l2-expand"
              onClick={() => setFullViewOpen(true)}
              title="Open in full-screen rings"
              aria-label="Expand to full-screen rings"
            >
              <Maximize2 aria-hidden />
            </button>
            <button
              type="button"
              className="t3-l2-hdr-btn t3-l2-close"
              onClick={onClose}
              aria-label="Close peek"
            >
              <X aria-hidden />
            </button>
          </div>
        </div>
        <div className="t3-l2-body">
          {panelContacts.length === 0 ? (
            <div className="t3-l2-empty">
              <b>
                {viewerLabel
                  ? `${viewerLabel.split(" ")[0]} is not mapped to anyone here yet`
                  : "You are not mapped to anyone here yet"}
              </b>
              <span>
                Add a mapping in the Coverage Matrix, then come back to assess.
              </span>
            </div>
          ) : (
            <div className="t3-l2-rings-layout">
              <div className="t3-l2-rings-canvas">
                <RelationshipRingsGraph
                  contacts={panelContacts}
                  mode="panel"
                  considerMeddpiccRole={considerMeddpiccRole}
                  onMappedContactClick={contact =>
                    onAssessRingContact?.(account, contact)
                  }
                  userName={userName}
                  centerUserStakeholder={centerUserStakeholder}
                />
              </div>
              <RelationshipRingsLegend variant="panel" />
            </div>
          )}
        </div>
        <div className="t3-l2-ftr">
          <div className="t3-l2-ftr-meta">
            <span
              className="t3-l2-ftr-chip"
              style={getRlBandPillStyle(account.rlAggregate)}
            >
              {formatAggregateRl(account.rlAggregate)}
            </span>
            <span className={`t3-l2-ftr-chip ${fanChipClass}`}>
              {viewerLabel
                ? `${viewerLabel.split(" ")[0]}'s fan-out ${myMapped}/${fanOutCap}`
                : `Your fan-out ${myMapped}/${fanOutCap}`}
            </span>
            <span className="t3-l2-ftr-chip">Assessed {assessedCount}</span>
            {pendingCount > 0 && (
              <span className="t3-l2-ftr-chip warn">
                Pending {pendingCount}
              </span>
            )}
          </div>
          <button
            type="button"
            className="t3-l2-cta"
            onClick={() => onOpenAccount(account.id)}
          >
            {pendingCount > 0 ? "Assess pending" : "Open account"}
          </button>
        </div>
      </div>
      <RelationshipRingsFullModal
        open={fullViewOpen}
        account={account}
        contacts={fullContacts}
        considerMeddpiccRole={considerMeddpiccRole}
        onClose={() => setFullViewOpen(false)}
        onMappedContactClick={contact =>
          onAssessRingContact?.(account, contact)
        }
        userName={userName}
        centerUserStakeholder={centerUserStakeholder}
      />
    </>
  );
}
